import type { Session } from "@supabase/supabase-js";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api } from "../lib/api.js";
import { supabase } from "../lib/supabase.js";
import type { CurrentUser } from "../lib/types.js";
import { AuthContext, type AuthState } from "./authState.js";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * The profile — crucially the role — is fetched from the API rather than read
   * off the JWT. A token can be decoded and edited client-side; the server
   * reads the role from the database on every request, and this keeps the UI
   * consistent with that decision.
   */
  const loadProfile = useCallback(async (active: Session | null) => {
    if (!active) {
      setUser(null);
      return;
    }

    try {
      const { user: profile } = await api.me();
      setUser(profile);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void supabase.auth.getSession().then(async ({ data }) => {
      if (cancelled) return;

      setSession(data.session);
      await loadProfile(data.session);

      if (!cancelled) setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, next) => {
        setSession(next);
        void loadProfile(next);
      },
    );

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const value = useMemo<AuthState>(
    () => ({
      session,
      user,
      loading,
      isAdmin: user?.role === "admin",

      signIn: async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw new Error(error.message);
      },

      signUp: async (email, password, displayName) => {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          // Read by the `handle_new_user` trigger to seed profiles.display_name.
          options: { data: { display_name: displayName } },
        });

        if (error) throw new Error(error.message);

        return { needsEmailConfirmation: data.session === null };
      },

      signOut: async () => {
        await supabase.auth.signOut();
        setUser(null);
      },

      refresh: async () => {
        const { data } = await supabase.auth.getSession();
        setSession(data.session);
        await loadProfile(data.session);
      },
    }),
    [session, user, loading, loadProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
